import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { MessageCircle, Loader2, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface TelegramConnectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  telegramConnectUrl: string;
  onPhoneSubmit?: (phoneNumber: string) => void;
  userId?: string;
}

export const TelegramConnectModal = ({
  open,
  onOpenChange,
  telegramConnectUrl,
  onPhoneSubmit,
  userId,
}: TelegramConnectModalProps) => {
  const { toast } = useToast();
  const [agreedToMessages, setAgreedToMessages] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [widgetKey, setWidgetKey] = useState(0);
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const canUseTelegramWidget =
    host === "modelaiagency.com" ||
    host === "www.modelaiagency.com";

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setAgreedToMessages(false);
      setIsConnecting(false);
      setIsConnected(false);
      setWidgetKey(prev => prev + 1); // Force widget reload
    }
  }, [open]);

  // Handle Telegram OAuth callback
  const handleTelegramCallback = useCallback(async (telegramUser: any) => {
    if (!userId) {
      toast({
        title: "Error",
        description: "User ID not found",
        variant: "destructive",
      });
      return;
    }

    setIsConnecting(true);

    try {
      console.log('Sending Telegram data to edge function:', telegramUser);
      const { data, error } = await supabase.functions.invoke('telegram-login-callback', {
        body: {
          ...telegramUser,
          user_id: userId,
          phone_number: telegramUser.phone_number || null
        }
      });

      console.log('Edge function response:', data, error);

      // Handle edge function errors (including non-2xx responses)
      if (error) {
        // Try to parse error message from edge function response
        let errorMessage = "Failed to connect Telegram";
        if (error.message) {
          errorMessage = error.message;
        }
        throw new Error(errorMessage);
      }

      // Handle explicit error in response body
      if (data && !data.success) {
        throw new Error(data.error || 'Failed to connect');
      }

      if (data?.success) {
        setIsConnected(true);
        toast({
          title: "Telegram Connected!",
          description: `Connected as @${data.telegram_username}`,
        });
        
        // Trigger automations after telegram connect (event-driven)
        supabase.functions.invoke("run-automations").catch(err => console.error("Failed to run automations:", err));
        
        // Close modal after short delay
        setTimeout(() => {
          onOpenChange(false);
          window.location.reload();
        }, 1500);
      }
    } catch (error: any) {
      console.error('Telegram callback error:', error);
      
      // Parse specific error messages for better UX
      let errorTitle = "Connection Failed";
      let errorDescription = error.message || "Failed to connect Telegram";
      
      // Check for already connected error
      if (errorDescription.includes("already connected to another user")) {
        errorTitle = "Telegram Already Linked";
        errorDescription = "This Telegram account is already connected to another Model AI Agency account. Each Telegram account can only be linked to one account.";
      }
      
      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  }, [userId, toast, onOpenChange]);

  // Load Telegram Login Widget when agreed and modal is open
  useEffect(() => {
    if (!canUseTelegramWidget) return;
    if (!open || !agreedToMessages || !widgetContainerRef.current) return;

    // Clear any existing widget
    widgetContainerRef.current.innerHTML = '';

    // Set up the global callback
    (window as any).TelegramLoginWidget = {
      dataOnauth: (user: any) => {
        console.log('Telegram widget callback triggered:', user);
        handleTelegramCallback(user);
      }
    };

    // Create the script element for Telegram Widget
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', 'modelaiagency_agent_bot');
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-userpic', 'false');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', 'TelegramLoginWidget.dataOnauth(user)');
    script.async = true;

    widgetContainerRef.current.appendChild(script);

    return () => {
      delete (window as any).TelegramLoginWidget;
    };
  }, [open, agreedToMessages, handleTelegramCallback, widgetKey, canUseTelegramWidget]);

  if (isConnected) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-foreground">Telegram Connected!</h3>
            <p className="text-muted-foreground text-center mt-2">
              Your Telegram account has been successfully connected.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <MessageCircle className="w-8 h-8 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">
            Connect Your Telegram
          </DialogTitle>
          <DialogDescription className="text-center">
            Before you can apply for this creator, you need to connect your Telegram account. 
            This is required for communication and support.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-2">Why Telegram?</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Direct communication with our team</li>
              <li>• Quick support and updates</li>
              <li>• Access to exclusive creator resources</li>
            </ul>
          </div>

          <div className="flex items-start space-x-3 p-3 rounded-lg border border-border bg-muted/30">
            <Checkbox
              id="agree-messages"
              checked={agreedToMessages}
              onCheckedChange={(checked) => setAgreedToMessages(checked === true)}
              className="mt-0.5"
            />
            <Label 
              htmlFor="agree-messages" 
              className="text-sm leading-relaxed cursor-pointer"
            >
              I agree to receive messages via Telegram regarding my application, updates, and support communications. <span className="text-destructive">*</span>
            </Label>
          </div>

          {agreedToMessages ? (
            <div className="flex flex-col items-center gap-4">
              {isConnecting ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">Connecting...</span>
                </div>
              ) : (
                <>
                  {canUseTelegramWidget ? (
                    <>
                      <div ref={widgetContainerRef} key={widgetKey} className="flex justify-center py-2" />
                      <p className="text-xs text-muted-foreground text-center">
                        Click the button above to connect your Telegram account.
                      </p>
                    </>
                  ) : (
                    <>
                      <Button asChild className="w-full">
                        <a href={telegramConnectUrl} target="_blank" rel="noreferrer">
                          Open Telegram And Connect
                        </a>
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        Telegram widget is only available on the main domain. Use this button to connect via bot.
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-muted-foreground">
              Please agree to the terms above to continue.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
